import { createContext, type ReactNode, useContext, useMemo } from "react";
import type { Attribute } from "@/types/attribute";
import type { ColumnDef } from "./ColumnModel";
import { useDefaultReadOnlyColumnBuilder } from "./column-builders/custom/useDefaultReadOnlyColumnBuilder";
import useNumberColumnBuilder from "./column-builders/custom/useNumberColumnBuilder";
import useOrbitSupportProjectColumnBuilder from "./column-builders/custom/useOrbitSupportProjectColumnBuilder";
import useParentTaskColumnBuilder from "./column-builders/custom/useParentTaskColumnBuilder";
import useSelectOneColumnBuilder from "./column-builders/custom/useSelectOneColumnBuilder";
import useTextColumnBuilder from "./column-builders/custom/useTextColumnBuilder";
import useTimerColumnBuilder from "./column-builders/custom/useTimerColumnBuilder";
import { useAssigneeColumnBuilder } from "./column-builders/useAssigneeColumnBuilder";
import { useDueDateColumnBuilder } from "./column-builders/useDueDateColumnBuilder";
import useListsColumnBuilder from "./column-builders/useListsColumnBuilder";
import { usePriorityColumnBuilder } from "./column-builders/usePriorityColumnBuilder";
import { useStatusColumnBuilder } from "./column-builders/useStatusColumnBuilder";

// Type for column builder function
type ColumnBuilder = {
    build: (isVisible: boolean, attribute?: Attribute) => ColumnDef;
};

// Context type
interface ColumnBuildersContextType {
    priority: ColumnBuilder;
    status: ColumnBuilder;
    dueDate: ColumnBuilder;
    assignee: ColumnBuilder;
    //createdBy: ColumnBuilder;
    lists: ColumnBuilder;
    timer: ColumnBuilder;
    number: ColumnBuilder;
    text: ColumnBuilder;
    selectOne: ColumnBuilder;
    parentTask: ColumnBuilder;
    orbitSupportProject: ColumnBuilder;
    defaultReadOnly: ColumnBuilder;
}

// Create context
const ColumnBuildersContext = createContext<ColumnBuildersContextType | null>(null);

// Provider component
export function ColumnBuildersProvider({ children }: { children: ReactNode }) {
    // Initialize all column builders once per provider instance
    const priorityBuilder = usePriorityColumnBuilder();
    const statusBuilder = useStatusColumnBuilder();
    const dueDateBuilder = useDueDateColumnBuilder();
    const assigneeBuilder = useAssigneeColumnBuilder();
    const listsBuilder = useListsColumnBuilder();
    const timerBuilder = useTimerColumnBuilder();
    const numberBuilder = useNumberColumnBuilder();
    const textBuilder = useTextColumnBuilder();
    const selectOneBuilder = useSelectOneColumnBuilder();
    const parentTaskBuilder = useParentTaskColumnBuilder();
    const orbitSupportProjectBuilder = useOrbitSupportProjectColumnBuilder();
    const defaultReadOnlyBuilder = useDefaultReadOnlyColumnBuilder();

    // Memoize the context value to prevent unnecessary re-renders
    const value = useMemo(
        () => ({
            priority: priorityBuilder,
            status: statusBuilder,
            dueDate: dueDateBuilder,
            assignee: assigneeBuilder,
            lists: listsBuilder,
            timer: timerBuilder,
            number: numberBuilder,
            text: textBuilder,
            selectOne: selectOneBuilder,
            parentTask: parentTaskBuilder,
            orbitSupportProject: orbitSupportProjectBuilder,
            defaultReadOnly: defaultReadOnlyBuilder,
        }),
        [
            priorityBuilder,
            statusBuilder,
            dueDateBuilder,
            assigneeBuilder,
            listsBuilder,
            timerBuilder,
            numberBuilder,
            textBuilder,
            selectOneBuilder,
            parentTaskBuilder,
            orbitSupportProjectBuilder,
            defaultReadOnlyBuilder,
        ]
    );

    return <ColumnBuildersContext.Provider value={value}>{children}</ColumnBuildersContext.Provider>;
}

// Hook to use column builders
export function useColumnBuilders(): ColumnBuildersContextType {
    const context = useContext(ColumnBuildersContext);
    if (!context) {
        throw new Error("useColumnBuilders must be used within a ColumnBuildersProvider");
    }
    return context;
}
